// src/core/transports/DefaultTransport.ts
import {
  ITransport,
  TransportConfig,
  TransportStats,
  EventIngestionResponse,
} from '../../types/transport';
import { INHERIT_CONFIG } from '../../types/config';
import { ILogger } from '../../utils/logger';
import { QueuedEvent } from '@/types';

export const DefaultTransportConfig: TransportConfig = {
  apiEndpoint: INHERIT_CONFIG,
  batchConfig: {
    batchSize: 10,
    flushInterval: 10,
    maxQueueSize: 100,
  },
};

/**
 * Enhanced default transport mechanism with batching, retry logic, and error handling.
 * Supports event batching, exponential backoff, and comprehensive error recovery.
 */
export class DefaultTransport implements ITransport {
  name = 'DefaultTransport';
  private config: TransportConfig;
  private clientKey: string;
  private logger?: ILogger;

  private eventQueue: { payload: QueuedEvent; timestamp: number }[] = [];
  private flushTimer?: NodeJS.Timeout;
  private isShuttingDown = false;
  private isFlushing = false;

  // Statistics tracking
  private stats: TransportStats = {
    eventsSent: 0,
    eventsFailed: 0,
    batchesSent: 0,
    batchesFailed: 0,
    queueSize: 0,
    avgBatchSize: 0,
  };

  constructor(clientKey: string, config?: TransportConfig, logger?: ILogger) {
    this.clientKey = clientKey;
    this.logger = logger;
    this.config = { ...DefaultTransportConfig, ...config };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private async transport(payload: Record<string, any>[]) {
    const body = { events: payload, batch: true, timestamp: Date.now() };

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'x-client-key': this.clientKey,
      ...this.config.customHeaders,
    };
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.requestTimeout);

      const response = await fetch(this.config.apiEndpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data: EventIngestionResponse = await response.json();
      // Success
      this.stats.eventsSent += data.eventsProcessed;
      this.stats.eventsFailed += data.eventsFailed;
      this.stats.batchesSent++;
      this.stats.lastSuccessfulSend = Date.now();
    } catch (error) {
      this.stats.eventsFailed += payload.length;
      this.stats.batchesFailed++;
      this.stats.lastError = Date.now();
      throw error;
    }
  }

  /**
   * Sends the payload. If batching is enabled, queues the event for batching.
   * If batching is disabled, sends immediately.
   */
  public async send(payload: QueuedEvent): Promise<void> {
    if (this.isShuttingDown) {
      this.logger?.warn(
        'DefaultTransport: Transport is shutting down. Event will be dropped.',
        payload
      );
      return;
    }
    if (this.config?.batchConfig?.batchSize) {
      // When batch processing is enabled
      this.eventQueue.push({
        payload,
        timestamp: Date.now(),
      });
      if (this.eventQueue.length >= this.config.batchConfig.batchSize) {
        // If we have enough events to flush
        this.logger?.debug(`DefaultTransport: Flushing batch of ${this.eventQueue.length} events`);
        await this.flushBatch();
        if (this.flushTimer) {
          clearTimeout(this.flushTimer);
          this.flushTimer = undefined;
        }
      } else if (!this.flushTimer) {
        // Start the flush timer if not already running
        this.startFlushTimer();
      }
    } else {
      // batch processing is disabled
      // eslint-disable-next-line no-void
      void this.transport([payload]);
    }
  }

  /**
   * Forces immediate flush of all queued events
   */
  public async flush(): Promise<void> {
    if (this.isFlushing) {
      this.logger?.debug('DefaultTransport: Flush already in progress, waiting...');
      return;
    }
    this.logger?.debug('DefaultTransport: Manual flush requested');
    await this.flushBatch();
  }

  /**
   * Shuts down the transport, flushing all pending events
   */
  public async shutdown(): Promise<void> {
    this.logger?.debug('DefaultTransport: Shutdown initiated');
    this.isShuttingDown = true;

    // Clear timers
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = undefined;
    }

    // Flush remaining events
    await this.flush();

    this.logger?.debug('DefaultTransport: Shutdown complete');
  }

  /**
   * Get current transport statistics
   */
  public getStats(): TransportStats {
    return {
      ...this.stats,
      queueSize: this.eventQueue.length,
      avgBatchSize: this.stats.batchesSent > 0 ? this.stats.eventsSent / this.stats.batchesSent : 0,
    };
  }

  /**
   * Configure transport settings at runtime
   */
  public configure(newConfig: Partial<TransportConfig>): void {
    // Update configuration
    Object.assign(this.config, newConfig);

    this.logger?.debug('DefaultTransport: Configuration updated', newConfig);
  }

  /**
   * Start the periodic flush timer
   */
  private startFlushTimer(): void {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
    }

    this.flushTimer = setTimeout(() => {
      if (!this.isShuttingDown && this.eventQueue.length > 0) {
        this.flushBatch().catch(error => {
          this.logger?.error('Failed to flush batch on timer:', error);
        });
      }
      if (!this.isShuttingDown) {
        this.startFlushTimer(); // Restart timer
      }
    }, this.config.batchConfig.flushInterval);
  }

  /**
   * Flush current batch of events
   */
  private async flushBatch(): Promise<void> {
    if (this.eventQueue.length === 0 || this.isFlushing) {
      return;
    }

    this.isFlushing = true;
    const batch = this.eventQueue.splice(0, this.config.batchConfig?.batchSize);
    this.stats.queueSize = this.eventQueue.length;

    this.logger?.debug(`DefaultTransport: Flushing batch of ${batch.length} events`);

    try {
      await this.transport(batch.map(item => item.payload));
    } catch (error) {
      this.logger?.error('DefaultTransport: Batch flush failed:', error);
    } finally {
      this.isFlushing = false;
    }
  }
}
